import React, { useContext, useState } from 'react';
import { TextField, Typography, Alert } from '@mui/material';
import {
    ForgotPasswordLink,
    LoginContainer,
    LoginWrapper,
    LogoContainer,
    FormContainer,
    LoginButton,
    UsernameContainer,
} from './Styled';
import { LogoPlaceholder } from '../../shared/svg/LogoPlaceholder';
import { authService } from '../../services/authService';
import { jwtDecode } from 'jwt-decode';
import apiMethods from '../../utils/api';
import { AuthenticationContext } from '../../provider/AuthenticationProvider';
import logoImg from '../../shared/svg/AppLogo/management.png';
import { warning } from '../../utils/toast';
import { useNavigate } from 'react-router-dom';

interface LoginFormData {
    username: string;
    password: string;
}

interface LoginProps {
    setIsSignedIn: Function;
}

export const Login = ({ setIsSignedIn }: LoginProps) => {
    const { setUser } = useContext(AuthenticationContext);
    const [formData, setFormData] = useState<LoginFormData>({
        username: '',
        password: '',
    });
    const [error, setError] = useState<string>('');
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const navigate = useNavigate();

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);

        try {
            const { access, force_change_password } = await authService.login(formData);

            // Force user to change password if necessary
            if (force_change_password) {
                warning("Thông báo", "Vui lòng đổi mật khẩu để tiếp tục sử dụng hệ thống");
                navigate('/change-password');    
                setIsLoading(false);
                setIsSignedIn(false);
                return;
            }

            // Fetch user data
            const decodedToken: any = jwtDecode(access);
            const userId = decodedToken.user_id;
            const response: any = await apiMethods.get(`/users/${userId}`);

            // store user data
            localStorage.setItem('user', JSON.stringify(response));
            setUser(response);

            setIsSignedIn(true);
        } catch (err) {
            setError('Tên đăng nhập hoặc mật khẩu không đúng');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <LoginContainer maxWidth="xs">
            <LoginWrapper>
                <LogoContainer>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <img src={logoImg} alt="" width={'32px'} />
                    </div>
                    <Typography variant="h1">PMS</Typography>
                </LogoContainer>

                <FormContainer onSubmit={handleSubmit}>
                    {error && (
                        <Alert
                            severity="error"
                            sx={{
                                mb: 2,
                                '& .MuiAlert-message': {
                                    color: '#d32f2f',
                                    fontSize: '14px',
                                },
                                backgroundColor: '#fdeded',
                                padding: '6px 16px',
                            }}
                        >
                            {error}
                        </Alert>
                    )}

                    <UsernameContainer>
                        <TextField
                            fullWidth
                            label="Tên đăng nhập"
                            name="username"
                            variant="outlined"
                            value={formData.username}
                            onChange={handleInputChange}
                            required
                            disabled={isLoading}
                            autoComplete="username"
                            sx={{
                                '& .MuiFormLabel-asterisk': {
                                    color: '#d32f2f',
                                },
                            }}
                        />
                        <TextField
                            fullWidth
                            label="Mật khẩu"
                            name="password"
                            type="password"
                            variant="outlined"
                            value={formData.password}
                            onChange={handleInputChange}
                            required
                            disabled={isLoading}
                            autoComplete="current-password"
                            sx={{
                                '& .MuiFormLabel-asterisk': {
                                    color: '#d32f2f',
                                },
                            }}
                        />
                    </UsernameContainer>

                    <ForgotPasswordLink to="/forgot-password">Quên mật khẩu?</ForgotPasswordLink>

                    <LoginButton type="submit" disabled={isLoading}>
                        {isLoading ? 'Đang đăng nhập...' : 'Đăng Nhập'}
                    </LoginButton>
                </FormContainer>
            </LoginWrapper>
        </LoginContainer>
    );
};
